/**
 * 视频模式 —— **全站唯一一份视频模式清单**。
 *
 * ---------------------------------------------------------------------------
 * 为什么有这个文件
 * ---------------------------------------------------------------------------
 * 2026-09-29 用户指出侧栏「视频」分组里的两条入口其实是一件事：
 *     带货短视频（`/short-video`）+ 商品视频（`/main-video`）
 * 两者共用同一条操作路径（引擎 → 素材 → 内容与场景 → 参数 → 提交），
 * 差别只有「产出什么形态」（15 秒竖版带货 vs 8 秒方版展示），
 * 却被拆成两个页面、两条侧栏入口、两套 `jobsFor` 键。
 * 合并后侧栏只留一条入口 `/video-studio`。
 *
 * 同一天早些时候的「主图 / 套图 / 详情」三页合一是同一个动作的第一次 ——
 * 模式清单的真源写法也照它（`config/studioModes.ts`），两份文件结构刻意保持一致。
 *
 * ---------------------------------------------------------------------------
 * 2026-10-05：页面内那排 chip-row 也撤了 —— 两档**彻底并成一档**
 * ---------------------------------------------------------------------------
 * 上一版虽然共用一条路由，页面里仍留着一排「带货短视频 / 商品视频」的 chip 让用户选。
 * 用户看了一轮之后的判词是「这两个感觉差不多啊，需要合并」—— 对照当时的模板清单，
 * 他说得对：`vt-08 3C 数码硬核开场` / `vt-10 家居好物实测` 与商品视频档的
 * `mv-01 白底产品展示` / `mv-03 生活场景植入` / `mv-04 细节推近特写` 是同一类拍法，
 * 差别只在时长与画幅；两个 chip 切来切去，用户看到的是「差不多」的同一批东西。
 *
 * 所以这一版：
 *   1. **`VIDEO_MODES` 里删掉 `main-video` 条目**，只剩 `short-video`（主力档）
 *      与 `video-replicate`（独立入口）。`STUDIO_VIDEO_MODES` 因此只剩一条，
 *      页面的 chip-row 自动不再渲染（见 VideoStudio 里那条长度判断）。
 *   2. **`main-video` 这个 slug 本身不能删** —— 它是资产分类键、后端 `app_kind`
 *      查表键与报价键，**旧任务与旧上传里还写着它**。它现在的容身处是
 *      `config/assetTaxonomy.ts` 的历史 slug 段 + 后端 `app/taxonomy.py` /
 *      `app/registry.py`，那三处本来就留着。前端这边认不出 `?mode=main-video`
 *      时由 `getStudioVideoMode()` 回落主力档，旧书签 / 旧分享链接照样能打开。
 *   3. **右栏不再按档位 slug 收窄** —— 主力档的取数是「本页全部视频任务」，
 *      否则用户切过来会发现之前在商品视频档提交的记录全不见了。见页面里的
 *      `STUDIO_VIDEO_SLUGS`。
 */

/** 两个条目：合并页唯一的主力视频档 + 独立入口的视频复刻 */
export type VideoModeId = 'short-video' | 'video-replicate'

export type VideoMode = {
  id: VideoModeId
  /** chip 上的名字。合并页的页签名也用它 */
  name: string
  /** 悬停说明（`title`）—— 一句话说清这一档产出什么 */
  desc: string
  /**
   * 提交用的 app_slug。**必须与后端已注册的一致**：
   * `app/registry.py` 的 APP_KIND 与 `app/taxonomy.py` 的 slug 表里，
   * 这三个值都在，改名等于把资产分类与报价一起改掉。
   */
  appSlug: string
  /** 场景清单 —— 各档的主力场景不同，chip 直连这里，页面里不写第二份 */
  scenes: string[]
  /** 默认时长（秒）。按秒计价，换档必须跟着换，否则报价口径串档 */
  duration: number
  /** 默认画面比例。带货短视频竖版、商品视频方版 */
  ratio: string
  /** 默认投放平台 —— 各档主力渠道不同，先给对的，用户可改 */
  platform: string
  /**
   * 是否必须传参考视频（决定素材卡的性质）
   */
  needVideo: boolean
  /** 独立入口页：不进合并页的 chip-row */
  standalone: boolean
}

export const VIDEO_MODES: VideoMode[] = [
  {
    id: 'short-video',
    name: '带货短视频',
    desc: 'TVC → UGC 软广 → 纯带货 → 种草 → 模特带货 → 广告投放，多段式结构；也覆盖主图位商品视频',
    appSlug: 'short-video',
    // 公司清单 三、视频类第 2 条「模特手持产品带货视频」是单独一条需求，
    // 上一版只有「手持跟随」——那是运镜（镜头怎么动），不是场景（谁在演）。
    //
    // 2026-10-05 并档后，这里接上原商品视频档的四个场景（产品展示 / 360° 旋转展示 /
    // 场景展示 / 细节推近）。**不做语义去重**：前八个是「谁在演」的带货路数，
    // 后四个是「产品怎么拍」的展示路数，层级不同，合并成一份只是省掉一次切档。
    // 顺序即 chip 排列顺序，`defaultsOf()` 取 `scenes[0]`，所以前八个一个字都不能挪。
    scenes: [
      'TVC 品牌质',
      'UGC 软广',
      '纯带货口播',
      '种草氛围',
      '模特手持产品',
      '模特使用产品',
      '信息流广告',
      '促销视频',
      '产品展示',
      '360° 旋转展示',
      '场景展示',
      '细节推近',
    ],
    /*
      2026-10-10：15 → 10 秒。

      两个原因，缺一不可：
      ① **默认档的钱**：按秒计价，默认时长直接乘进报价。默认引擎与清晰度
         同一天一起下调（见 `modelFamilies.DEFAULT_FAMILY_ID.video` 与
         `DEFAULT_VIDEO_RESOLUTION`），15 秒那一版单这一项就把首屏报价顶到最高；
      ② **默认引擎只出 6/8/10 秒**（Google Omni）—— 留 15 秒的话，新用户
         一进页面时长就在模型不支持的档上，提交才发现上游不收。
         10 秒同时满足这两条，也仍够一条完整的带货脚本（15 秒那条
         `vt-07` 模板自带时长，点它就会把引擎换成能跑 15 秒的档）。
    */
    duration: 10,
    ratio: '9:16',
    platform: 'douyin',
    needVideo: false,
    standalone: false,
  },
  {
    id: 'video-replicate',
    name: '视频复刻',
    desc: '上传参考视频，复刻镜头结构与节奏，替换为你的产品',
    appSlug: 'video-replicate',
    scenes: ['结构复刻', '节奏复刻'],
    /* 同上（2026-10-10，15 → 10）：复刻档不渲染引擎下拉，它的引擎就是
       `DEFAULT_FAMILY_ID.video`（Google Omni，只出 6/8/10 秒）——
       留 15 秒会让这一档一进来就落在模型不支持的档上。 */
    duration: 10,
    ratio: '9:16',
    platform: 'douyin',
    needVideo: true,
    standalone: true,
  },
]

/**
 * 合并页（`/video-studio`）的档位 —— **顺序 = chip-row 的排列顺序**。
 *
 * 2026-10-05 起只剩一条（`short-video`），所以页面那排 chip 不再渲染。
 * 这个常量**不删**：① 它仍是「哪些档属于合并页」的唯一判据（`standalone` 之外）；
 * ② `scripts/export-template-count.ts` 按它算各档模板条数；
 * ③ 以后真要再分档，加一条进 `VIDEO_MODES` 就自动回来，页面不用改。
 */
export const STUDIO_VIDEO_MODES = VIDEO_MODES.filter((m) => !m.standalone)

/**
 * 合并页**可能写进任务的**全部 app_slug —— 右栏取数用。
 *
 * 为什么不是一个值：`main-video` 虽然不再是页面上的一档，但**旧任务里还写着它**。
 * 右栏若只按当前档的 slug 收窄，用户切过来会以为历史记录被清空了。
 * 所以主力档的取数是「本页全部、slug 落在这个集合里的任务」——
 * 集合里刻意**不含** `storyboard`（那是分镜参考图，是视频任务的输入不是产出，
 * 混进「本轮结果」会让「生成了几支片子」变成假数字）。
 */
export const STUDIO_VIDEO_SLUGS: string[] = ['short-video', 'main-video']

/** 合并页的默认落地档 */
export const DEFAULT_VIDEO_MODE: VideoModeId = 'short-video'

export const VIDEO_MODE_IDS: VideoModeId[] = VIDEO_MODES.map((m) => m.id)

/* -------------------------------------------------------------------------- */
/* 视频参数清单 —— 与档位同源，供页面控件与模板预设共用                          */
/* -------------------------------------------------------------------------- */

/**
 * 视觉风格 / 时长 / 比例三份清单原本**只长在 `VideoStudio.tsx` 里**。
 *
 * 2026-09-29 加视频模板时它们必须第二次出现：模板预设要逐条写死「这个模板配哪种风格
 * 与时长」。如果模板里再抄一份字面量，页面下拉里改一个词、模板就静默失配 ——
 * 与本文件开头写的「同一份清单不许两处各写一遍」是同一类复发。
 *
 * 所以清单上提到这里：页面控件与模板预设都从这里取，`VideoStyle` 联合类型让模板
 * 写错一个词**在 tsc 阶段就报错**，不用等到运行时才发现下拉不匹配。
 *
 * ⚠️ 2026-10-09：原来还有一份 `VIDEO_CAMERAS`（固定机位 / 手持跟随 / 推拉运镜），
 * 用户口径「去掉运镜这一项」—— 控件、表单字段与提示词里的【运镜】参数一起删掉了
 * （模板各自的镜头脚本里那句「机位怎么走」是**这条模板自己的分镜约束**，不是用户要
 * 选的一项，保留在 `videoTemplates` 的 `shot` 文案里）。
 */
export const VIDEO_STYLES = [
  '清爽电商棚拍',
  '暖调生活感',
  '高对比潮流',
  '极简白底科技感',
  '自然光实拍 UGC',
] as const

/**
 * 常用时长档位。按秒计价，所以这里是**数字**（控件的 value 由调用方 String() 化）。
 *
 * ⚠️ 2026-10-09 起这只是**「一键选」的常用档**，不再是可以选的全部：时长控件
 * （`DurationInput`）旁边常驻一个数字输入框，用户能直接敲任意秒数。
 * 真正决定「这个模型能出多少秒」的是后端逐模型下发的 `capabilities.duration_min /
 * duration_max / duration_options`（见 `api/types.ts`），本清单只负责给一排常见的。
 */
export const VIDEO_DURATIONS = [5, 8, 10, 15] as const

/**
 * 比例的**兜底清单**（后端没下发 `capabilities.aspect_ratios` 时用）。
 *
 * 真正可选哪几种由**所选模型**决定（后端逐模型下发，见 `api/types.ts`）；
 * 且 2026-10-09 起用户口径「能自由填比例」—— 控件（`RatioInput`）是
 * 「下拉列模型能出的 + 旁边一个自由输入框」，所以这份清单**既不是全部、
 * 也不是边界**，只是模型清单缺失时给下拉一个不空的候选集。
 */
export const VIDEO_RATIOS = ['1:1', '9:16', '16:9'] as const

/**
 * 视频清晰度 —— **兜底清单 + 归一化标签**。
 *
 * 真正可选哪几档由**所选模型**决定：后端 registry 的模型表带
 * `capabilities.resolutions`（值抄自上游 catalog 实测）。14 个贰春模型里
 * 只有 3 个同时支持 480/720/1080，MiniMax-H3 只有 768P，运营商渠道那几档
 * 更是只有一个值。所以这份清单**只是「后端没说时的默认三档」**，
 * 页面必须优先渲染模型给的值。
 *
 * ⚠️ 上游各模型的写法大小写不统一（同一个平台里 `720P` 和 `720p` 并存），
 * 而按原值直接渲染会在一排 chip 里出现「720P / 720p」两种长相。
 * 所以控件里 **value 用模型原值、label 归一化成大写** ——
 * 显示统一，提交时传回去的仍是模型认的那个原值。
 */
export const VIDEO_RESOLUTIONS = ['480P', '720P', '1080P'] as const

/** 归一到展示用的大写形态（`720p` → `720P`）。只影响显示，不改提交值。 */
export function resolutionLabel(value: string): string {
  return value.toUpperCase()
}

/**
 * 默认清晰度 —— **720P（2026-10-10 由 1080P 下调）**。
 *
 * 原来按「尽量高」取 1080P。这一条本身没错，错的是它**与默认引擎、默认时长
 * 叠在一起**的后果：1080P 在三档里最贵（Wan 3.0 是 ¥0.158/0.4/0.8 每秒），
 * 于是「进页面什么都不改直接点生成」= 全场最贵的组合（¥12.00）。
 * 用户口径：「第一次试流程的人，一按就是 12 块，这不符合人性。」
 *
 * 现在取 720P：它仍是**标准交付档**（不是拿 480P 糊弄），但不再是价格最高的一档。
 * 要 1080P 的用户在「输出参数」页一眼就能换 —— 那一页的第一个控件就是它。
 *
 * ⚠️ **必须再经模型能力过滤** —— 选了只有 768P 的 MiniMax-H3、或只有 480p 的
 * seedance 系时，页面会把这项换成模型给的第一个值（见 VideoStudio 的归一 effect）。
 * 不这么做的话，界面停在一个模型根本不支持的档位上，提交时才由上游报错，用户白等一轮。
 */
export const DEFAULT_VIDEO_RESOLUTION = '720P'

export type VideoStyle = (typeof VIDEO_STYLES)[number]
export type VideoRatio = (typeof VIDEO_RATIOS)[number]

/**
 * 按 id 取档位。**认不出就回落默认档，不抛错** ——
 * 调用方之一是 `lib/appSlug.ts`，它跑在「用户可能手改过地址栏」的路径上，
 * 地址栏里塞个 `?mode=xxx` 不该把整页干崩。
 */
export function getVideoMode(id?: string | null): VideoMode {
  return VIDEO_MODES.find((m) => m.id === id) ?? VIDEO_MODES.filter((m) => !m.standalone)[0]
}

/**
 * 取**合并页**的档位。
 *
 * 与 `getVideoMode` 的差别只有一条：`video-replicate` 与认不出的值都**回落默认档**。
 * 为什么必须有这一层：合并页的档位来自地址栏，而地址栏是用户可以手改的 ——
 * 把 `?mode=video-replicate` 打到合并页上，若直接认，页面会变成「必传参考视频」
 * 的复刻表单，但地址、侧栏高亮、任务键都还是合并页的，等于凭空多出一个假页面。
 * 复刻只从它自己的路由进来（`/video-replicate`，不读 mode）。
 *
 * 2026-10-05 并档之后这一层还多担了一件事：**接住已下架档位的旧链接**。
 * `?mode=main-video` / 老书签 `/main-video` / 分享出去的卡片地址里都可能写着它，
 * `VIDEO_MODES` 里已经没有这一条，于是「认不出」→ 回落主力档，页面照常打开。
 * 这正是本条不做特判就能兼容的原因：兼容靠的是**回落**，不是再留一条僵尸档位。
 */
export function getStudioVideoMode(id?: string | null): VideoMode {
  const m = VIDEO_MODES.find((x) => x.id === id)
  return m && !m.standalone ? m : getVideoMode(DEFAULT_VIDEO_MODE)
}

/**
 * 换档位时要一并铺开的默认值。
 *
 * 为什么整组换而不是只换场景：**这些参数是相互咬合的一套**——
 * 15 秒 / 9:16 / 抖音属于带货短视频，8 秒 / 1:1 / 淘宝属于商品视频。
 * 只换场景不动时长，就会出现「模式是商品视频、时长却是 15 秒」的混搭，
 * 报价也跟着串档（按秒计价）。所以换档 = 换整套。
 */
export function defaultsOf(id: VideoModeId) {
  const m = getVideoMode(id)
  return {
    mode: m.id,
    scene: m.scenes[0],
    duration: m.duration,
    aspectRatio: m.ratio,
    platform: m.platform,
  }
}
